/**
 * <asd-milestone-bar> keeps the server-rendered milestone bar in sync with the cart.
 *
 * The markup is rendered by snippets/asd-milestone-bar.liquid, but its tier thresholds
 * (data-amount on each node) are always in the SHOP's currency (CAD): Liquid has no way to read
 * the buyer's exchange rate, only window.Shopify.currency.rate does, client-side. So this element
 * always re-renders once on connect - even before any cart event - converting each threshold with
 * that rate before comparing it to the total (cart.items_subtotal_price, which Shopify already
 * expresses correctly in the buyer's active currency, both server- and client-side).
 *
 * Keep the fill maths in step with the Liquid in the snippet (aside from the currency conversion,
 * which only makes sense to do here).
 */
class AsdMilestoneBar extends HTMLElement {
  connectedCallback() {
    document.addEventListener('shopify:cart:lines-update', this.#onCartEvent);
    document.addEventListener('shopify:cart:discount-update', this.#onCartEvent);
    window.addEventListener('pageshow', this.#onPageShow);

    // Server-rendered total is already in the right currency; only the tier thresholds need
    // converting, so redo the render locally instead of an extra /cart.js round trip.
    this.#render(Number(this.dataset.total) || 0);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.#onCartEvent);
    document.removeEventListener('shopify:cart:discount-update', this.#onCartEvent);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // Restored from the back/forward cache: the cart may have changed since this was painted.
    if (event.persisted) this.#refresh();
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartEvent = (event) => {
    // Wait for the theme's own cart request to settle so /cart.js reflects the change.
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => this.#refresh());
  };

  async #refresh() {
    try {
      const cartUrl = window.Theme?.routes?.cart_url ?? '/cart';
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(Number(cart.items_subtotal_price) || 0);
    } catch {
      // Leave the last known state in place.
    }
  }

  // Fixed, approximate CAD-per-USD rate used only to convert an international threshold into the
  // buyer's local currency (not live-fetched - see #tierAmount). Update this if it drifts a lot.
  static #CAD_PER_USD = 1.37;

  /**
   * A node's data-amount is entered by the merchant in CAD (Theme settings > Milestone bar).
   * Canada sees that number exactly. Everyone else sees the SAME number in USD instead: exact for
   * the US market, and for every other currency, converted from USD and rounded to a whole unit -
   * international thresholds don't need to land on an exact converted CAD figure.
   */
  #tierAmount(node) {
    const raw = Number(node.dataset.amount); // CAD cents
    const active = window.Shopify?.currency?.active;
    if (!active || active === 'CAD' || active === 'USD') return raw;

    const cadToActive = Number(window.Shopify?.currency?.rate);
    if (!Number.isFinite(cadToActive) || cadToActive <= 0) return raw;

    const usdToActive = cadToActive * AsdMilestoneBar.#CAD_PER_USD;
    return Math.round((raw * usdToActive) / 100) * 100; // nearest whole unit, no cents
  }

  /** @param {number} total - cart subtotal after item-level discounts, in the buyer's currency, minor units (cents) */
  #render(total) {
    const nodes = [...this.querySelectorAll('[data-ms-node]')];
    if (!nodes.length) return;

    let fill = 0;
    let reachedAmount = 0;
    let reachedPos = 0;
    /** @type {{ amount: number, label: string } | null} */
    let next = null;

    nodes.forEach((node, index) => {
      const amount = this.#tierAmount(node);
      const pos = ((index + 1) * 100) / nodes.length;
      const reached = total >= amount;

      node.toggleAttribute('data-reached', reached);

      const amountEl = node.querySelector('.asd-ms__amount');
      if (amountEl) amountEl.textContent = this.#money(amount);

      if (reached) {
        fill = pos;
        reachedAmount = amount;
        reachedPos = pos;
      } else if (!next) {
        next = { amount, label: node.dataset.label ?? '' };
        fill = reachedPos + ((total - reachedAmount) * (pos - reachedPos)) / (amount - reachedAmount);
      }
    });

    fill = Math.round(fill * 100) / 100;

    const fillEl = this.querySelector('[data-ms-fill]');
    if (fillEl instanceof HTMLElement) fillEl.style.width = `${fill}%`;
    this.querySelector('[data-ms-rail]')?.setAttribute('aria-valuenow', String(Math.round(fill)));
    this.dataset.total = String(total);

    let template;
    let amountText = '';
    let reward = '';
    if (total === 0) {
      template = this.dataset.msgStart;
      amountText = this.#money(this.#tierAmount(nodes[0]));
      reward = nodes[0].dataset.label ?? '';
    } else if (next) {
      template = this.dataset.msgAway;
      amountText = this.#money(next.amount - total);
      reward = next.label;
    } else {
      template = this.dataset.msgDone;
    }

    const messageEl = this.querySelector('[data-ms-message]');
    if (messageEl && template != null) {
      messageEl.textContent = template.replaceAll('{amount}', amountText).replaceAll('{reward}', reward);
    }
  }

  /**
   * Formats minor units in the active currency, matching Liquid's money_without_trailing_zeros.
   * @param {number} cents
   */
  #money(cents) {
    const value = cents / 100;
    const whole = Number.isInteger(value);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(value);
    } catch {
      return `$${whole ? value : value.toFixed(2)}`;
    }
  }
}

if (!customElements.get('asd-milestone-bar')) {
  customElements.define('asd-milestone-bar', AsdMilestoneBar);
}
